import React from 'react';
import PropTypes from 'prop-types';
import { ClassNames, keyframes } from '@emotion/core';
import { useTheme } from 'emotion-theming';

const sweep = keyframes`
  0% { background-position: -468px 0; }
  100% { background-position: 468px 0; }
`;

/**
 * Drop-in replacement for react-shimmer-effect's <Shimmer>. The library
 * hardcodes a near-white gradient in an injected JSS class, which flashes
 * harshly in dark mode — and its class names are minified in production
 * builds, so the gradient can't be retargeted with a CSS override. Light
 * values below are copied verbatim from the library; dark values sit on the
 * elevation ladder (surfaceInput base, slightly brighter sweep).
 *
 * Children are cloned with the shimmer class prepended, so a child's own
 * styles (display, sizing) win any conflicts — same layout as the library.
 */
const Shimmer = ({ children, className, display }) => {
  const theme = useTheme();
  const isDark = !!theme?.isDark;

  const background = isDark
    ? (theme.colors?.surfaceInput || 'rgb(44, 44, 44)')
    : '#f2f2f2';
  const gradient = isDark
    ? `linear-gradient(to right, ${theme.colors?.surfaceInput || 'rgb(44, 44, 44)'} 40%, rgb(56, 56, 56) 50%, ${theme.colors?.surfaceInput || 'rgb(44, 44, 44)'} 60%)`
    : 'linear-gradient(to right, #eeeeee 40%, #dddddd 50%, #eeeeee 60%)';

  return (
    <ClassNames>
      {({ css, cx }) => {
        const shimmerClass = css`
          background: ${background};
          display: ${display};
          background-image: ${gradient};
          background-size: 800px 104px;
          background-repeat: no-repeat;
          animation: ${sweep} 2s infinite linear;
        `;
        return React.Children.map(children, (child) => (
          React.isValidElement(child)
            ? React.cloneElement(child, {
              className: cx(shimmerClass, className, child.props.className),
            })
            : child
        ));
      }}
    </ClassNames>
  );
};

Shimmer.propTypes = {
  children: PropTypes.node,
  /** Merged onto the cloned child, matching react-shimmer-effect, which
   * forwards a className the same way. */
  className: PropTypes.string,
  /** The library hardcodes inline-block; a few call sites override it with a
   * descendant rule targeting the library's generated class name, which a
   * themed replacement can't be targeted by. */
  display: PropTypes.string,
};

Shimmer.defaultProps = {
  children: null,
  className: undefined,
  display: 'inline-block',
};

export default Shimmer;
